import { Injectable, NgZone } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Store } from '@ngxs/store';
import { GpAsteriskService, GpAsteriskState, GpCallModel, SoftphoneConfigModel } from 'gp-asterisk';
import { ProfileState } from 'shared';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AudioRoute, isAudioRouteAvailable } from './audio-route.plugin';
import { CallHistory, CallHistoryEntry, CallHistoryInfo } from './call-history';
import { CallQuality, CallQualityMonitor, QualitySource, webQualitySource } from './call-quality';
import { diagLog, isNativeMediaAvailable, nativeSessionDescriptionHandlerFactory, NativeSessionDescriptionHandler } from './native-session-description-handler';
import { Invitation, Inviter, Registerer, RegistererState, Session, SessionDescriptionHandlerFactory, SessionState, UserAgent, Web } from 'sip.js';

export type SoftphoneStatus = 'off' | 'connecting' | 'registered' | 'error';

export interface SoftphoneCall {
  number: string;
  displayName: string;
  // calling — лінія з АТС відкрита, йде виклик клієнта (час розмови ще не рахуємо)
  status: 'ringing' | 'connecting' | 'calling' | 'active';
  muted: boolean;
  speaker: boolean;
  startedAt?: number;
  // підказка під статусом (напр. «Дзвонить у колеги на телефоні»)
  note?: string;
  // якість зв'язку під час розмови (call-quality.ts): слабкий/поганий — показуємо на екрані дзвінка
  quality?: CallQuality;
}

export interface SoftphoneState {
  status: SoftphoneStatus;
  message?: string;
  call: SoftphoneCall | null;
}

const ENABLED_KEY = 'softphone_enabled';
const RECONNECT_DELAY_MS = 5000;
const ICE_SERVERS = ['stun:stun.l.google.com:19302'];
// скільки чекаємо, поки АТС прийме лінію застосунку
const LINE_TIMEOUT_MS = 15000;

/**
 * Вбудований телефон: SIP через WebRTC (SIP.js) до Asterisk по WSS.
 * Реєструється як PJSIP <номер>. Розмови з CRM (дзвінок клієнту, відповідь, прослуховування) йдуть через лінію,
 * яку відкриває сам застосунок (openLine) — АТС 16.13 не вміє дзвонити у WebSocket сама.
 * Етап 1: працює, поки застосунок відкритий (у фоні/закритому стані — сторонній застосунок або стаціонарний телефон).
 */
@Injectable({ providedIn: 'root' })
export class SoftphoneService {
  readonly state$ = new BehaviorSubject<SoftphoneState>({ status: 'off', call: null });
  // екран розмови згорнуто в смужку зверху (новий вхідний завжди розгортає)
  readonly minimized$ = new BehaviorSubject(false);

  private userAgent: UserAgent | null = null;
  private registerer: Registerer | null = null;
  private session: Session | null = null;
  private qualityMonitor: CallQualityMonitor | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = true;
  private domain: string | null = null;
  // власний внутрішній номер (з налаштувань АТС; у профілі його може ще не бути, якщо щойно створено)
  private username: string | null = null;
  private nativeMajorVersion?: Promise<number>;
  // застосунок побував у фоні після останнього підключення — з'єднання з АТС могло тихо «вмерти»
  // (iOS/Android заморожують WebSocket); перед дзвінком перепідключаємось одразу, а не чекаємо тайм-ауту
  private wasSuspended = false;
  // коли почалось поточне підключення: повторний виклик під час нього обривав його («due to shutdown» через 4–5 с)
  // і починав спочатку — при кожному відкритті застосунку «З'єднання з АТС» показувалось двічі
  private connectingSince = 0;
  // перепідключення попросили посеред розмови — виконаємо, щойно вона закінчиться
  private reconnectAfterCall = false;
  private micRecovering = false;
  private micRecoveries = 0;
  // розмова для «Недавніх» системного телефону (одна на withLine, навіть якщо лінію відкривали двічі)
  private history: CallHistoryEntry | null = null;
  private readonly ringtone = new Audio('shared/src/assets/sounds/calling.mp3');
  // WKWebView (iOS) відтворює WebRTC-звук лише з <audio playsinline>, вставленого в сторінку
  private readonly remoteAudio = SoftphoneService.createRemoteAudio();

  constructor(private asteriskService: GpAsteriskService, private store: Store, private zone: NgZone) {
    this.ringtone.loop = true;
    // клієнт підняв трубку — бекенд знімає з вихідного дзвінка isDial; тоді починаємо рахувати час
    this.store.select(GpAsteriskState.callList).subscribe(calls => this.checkClientAnswered(calls as GpCallModel[]));
    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) this.wasSuspended = true;
      if (isActive && this.isEnabled && !this.stopped) this.ensureConnected();
    }).catch(() => {});
  }

  // Лише нові нативні збірки: у них є дозвіл на мікрофон (старі збірки зі стору без нього на iOS аварійно закриваються).
  // Ознака нової збірки — плагін OTA-оновлень, який з'явився разом із дозволами.
  get isSupported(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('CapacitorUpdater');
  }

  get isEnabled(): boolean {
    return localStorage.getItem(ENABLED_KEY) === '1';
  }

  async setEnabled(enabled: boolean): Promise<void> {
    localStorage.setItem(ENABLED_KEY, enabled ? '1' : '0');
    if (!enabled) {
      await this.stop();
      return;
    }
    // немає номера чи SIP-акаунта — бекенд створить їх (для користувачів з доступом до АТС)
    this.patch({ status: 'connecting', message: 'Готуємо ваш номер на АТС…' });
    try {
      await firstValueFrom(this.asteriskService.provisionSoftphone());
    } catch (err: any) {
      this.patch({ status: 'error', message: err?.error?.message || 'Не вдалося створити номер на АТС' });
      return;
    }
    await this.start();
  }

  async start(): Promise<void> {
    if (!this.isSupported || !this.isEnabled) return;
    this.stopped = false;
    await this.connect();
  }

  async stop(): Promise<void> {
    this.stopped = true;
    this.clearReconnect();
    this.hangup();
    await this.registerer?.unregister().catch(() => {});
    await this.userAgent?.stop().catch(() => {});
    this.userAgent = null;
    this.registerer = null;
    this.patch({ status: 'off', message: undefined, call: null });
  }

  get isRegistered(): boolean {
    return this.state$.value.status === 'registered';
  }

  // Користувач увімкнув «Приймати дзвінки в застосунку» (на пристрої, що це підтримує)
  get isActive(): boolean {
    return this.isSupported && this.isEnabled && !this.stopped;
  }

  // Чекаємо реєстрації на АТС (після повернення з фону з'єднання відновлюється кілька секунд)
  // Застосунок міг бути «заморожений» у фоні: статус ще «підключено», а АТС уже закрила WebSocket —
  // тоді перепідключаємось і чекаємо нової реєстрації.
  async waitRegistered(timeoutMs = 8000, forceReconnect = false): Promise<boolean> {
    if (!this.isActive) return false;
    if (this.isLive && !forceReconnect) return true;
    if (forceReconnect || this.state$.value.status !== 'connecting') this.connect();
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      await new Promise(resolve => setTimeout(resolve, 250));
      if (this.isLive) return true;
    }
    return false;
  }

  // розмова, яка справді ще йде: сесію, що вже завершилась (подію про кінець могли не отримати), прибираємо
  private get hasLiveSession(): boolean {
    if (this.session?.state === SessionState.Terminated) {
      this.session = null;
      if (this.state$.value.call) this.patch({ call: null });
    }
    return !!this.session;
  }

  private get isLive(): boolean {
    return this.isRegistered && !!this.userAgent?.isConnected() && this.registerer?.state === RegistererState.Registered;
  }

  // Asterisk 16.13 не вміє сам дзвонити у WebSocket-з'єднання (ASTERISK-30065), тому з'єднання починаємо ми:
  // набираємо `link` — АТС приймає і тримає лінію, а бекенд підключає до неї розмову (дзвінок клієнту,
  // відповідь на вхідний, прослуховування). Promise завершується, коли АТС прийняла лінію.
  openLine(label: string, awaitClientAnswer = false): Promise<void> {
    if (!this.userAgent || !this.domain || !this.isRegistered) return Promise.reject(new Error('Телефон не підключений до АТС'));
    if (this.hasLiveSession) return Promise.reject(new Error('Ви вже розмовляєте'));
    const target = UserAgent.makeURI(`sip:link@${this.domain}`);
    if (!target) return Promise.reject(new Error('Невірна адреса АТС'));

    const inviter = new Inviter(this.userAgent, target, {
      sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
    });
    this.session = inviter;
    this.minimized$.next(false);
    this.patch({ call: { number: label, displayName: '', status: 'connecting', muted: false, speaker: false } });

    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('АТС не відповіла'));
        this.hangup();
      }, LINE_TIMEOUT_MS);
      this.track(inviter, () => { clearTimeout(timeout); resolve(); }, () => { clearTimeout(timeout); reject(new Error('Лінію не відкрито')); }, awaitClientAnswer);
      inviter.invite().catch(err => {
        console.error('[Softphone] line', err);
      diagLog(`line failed: ${err}`);
        clearTimeout(timeout);
        reject(err);
        this.hangup();
      });
    });
  }

  // Дія бекенду, що з'єднує розмову з телефоном: спершу відкриваємо лінію з застосунку (якщо телефон підключений).
  // Якщо бекенд відмовив — лінію закриваємо. history — дзвінок для «Недавніх» (прослуховування туди не пишемо).
  async withLine<T>(label: string, request: () => Promise<T>, awaitClientAnswer = false, history?: CallHistoryInfo): Promise<T> {
    // телефон увімкнений — завжди через нього (а не на SIP-номер/сторонній застосунок), навіть якщо саме перепідключається
    const useLine = this.isActive && !this.hasLiveSession;
    if (useLine) {
      if (history) this.history = CallHistory.start(history);
      try {
        // після фону старе з'єднання часто мертве: одразу свіже, інакше спроба через нього тягнеться до тайм-ауту
        const reconnect = this.wasSuspended && this.state$.value.status !== 'connecting';
        if (!await this.waitRegistered(8000, reconnect)) throw new Error('Вбудований телефон не підключений до АТС — перевірте зв’язок');
        try {
          await this.openLine(label, awaitClientAnswer);
        } catch {
          // з'єднання виявилось мертвим — перепідключаємось і пробуємо ще раз
          this.hangup();
          this.session = null;
          if (!await this.waitRegistered(8000, true)) throw new Error('Вбудований телефон не підключений до АТС — перевірте зв’язок');
          await this.openLine(label, awaitClientAnswer);
        }
      } catch (err) {
        this.finishHistory();
        throw err;
      }
    }
    try {
      return await request();
    } catch (err) {
      if (useLine) this.hangup();
      throw err;
    }
  }

  // Дзвінок клієнту через АТС у вбудованому телефоні — із записом і карткою клієнта, як із SIP-телефону
  // Вхідний дзвінок ще дзвонить (push) — заздалегідь готуємо свіже з'єднання з АТС, щоб після «Прийняти» звук був одразу
  async prepareForCall(): Promise<void> {
    if (!this.isEnabled || this.hasLiveSession) return;
    if (!this.isActive) {
      await this.start();
      return;
    }
    const reconnect = this.wasSuspended && this.state$.value.status !== 'connecting';
    await this.waitRegistered(8000, reconnect);
  }

  // Нативні збірки v5+ (з вбудованим телефоном): там клієнтам дзвонимо лише через нього
  async isSoftphoneOnlyBuild(): Promise<boolean> {
    if (!this.isSupported) return false;
    this.nativeMajorVersion ??= App.getInfo().then(info => parseInt(info.version, 10) || 0).catch(() => 0);
    return (await this.nativeMajorVersion) >= 5;
  }

  async callViaApp(phone: string, sipNumber = this.username || ''): Promise<void> {
    if (!sipNumber) throw new Error('Телефон у застосунку ще не підключений до АТС');
    await this.withLine(phone, () => firstValueFrom(this.asteriskService.toCall(phone, `PJSIP/${sipNumber}`)), true,
      { direction: 'outgoing', number: phone });
  }

  // ── дзвінок ────────────────────────────────────────────────────────────────
  async answer(): Promise<void> {
    if (!(this.session instanceof Invitation) || this.session.state !== SessionState.Initial) return;
    this.stopRingtone();
    this.patchCall({ status: 'connecting' });
    try {
      await this.session.accept({ sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } } });
    } catch (err) {
      console.error('[Softphone] answer', err);
      this.patch({ message: 'Немає доступу до мікрофона або з\'єднання з АТС' });
      this.hangup();
    }
  }

  // Розмова закінчилась на боці АТС (подія бекенду), а сигнал до застосунку міг загубитися (мертве з'єднання):
  // кладемо трубку, а якщо сесія за 2 с так і не завершилась — прибираємо екран дзвінка примусово
  endCall(): void {
    const session = this.session;
    if (!session) return;
    this.hangup();
    setTimeout(() => {
      if (this.session !== session || session.state === SessionState.Terminated) return;
      this.session = null;
      this.stopRingtone();
      this.remoteAudio.srcObject = null;
      if (isAudioRouteAvailable()) AudioRoute.reset().catch(() => {});
      this.finishHistory();
      this.patch({ call: null });
    }, 2000);
  }

  hangup(): void {
    const session = this.session;
    if (!session) return;
    this.stopRingtone();
    if (session instanceof Inviter && (session.state === SessionState.Initial || session.state === SessionState.Establishing)) {
      session.cancel().catch(() => {});
    } else if (session instanceof Invitation && session.state === SessionState.Initial) {
      session.reject().catch(() => {});
    } else if (session.state === SessionState.Established) {
      session.bye().catch(() => {});
    } else if (session.state === SessionState.Establishing) {
      (session as Invitation).reject?.().catch(() => {});
    }
  }

  setMinimized(minimized: boolean): void {
    this.minimized$.next(minimized);
  }

  toggleMute(): void {
    const call = this.state$.value.call;
    if (!call || !this.session) return;
    const muted = !call.muted;
    const handler = this.session.sessionDescriptionHandler;
    if (handler instanceof NativeSessionDescriptionHandler) {
      handler.setMuted(muted);
      this.patchCall({ muted });
      return;
    }
    (handler as Web.SessionDescriptionHandler | undefined)?.peerConnection?.getSenders().forEach(sender => {
      if (sender.track) sender.track.enabled = !muted;
    });
    this.patchCall({ muted });
  }

  // Гучномовець / розмовний динамік (лише в збірках із нативним плагіном AudioRoute)
  get canToggleSpeaker(): boolean {
    return isAudioRouteAvailable();
  }

  toggleSpeaker(): void {
    const call = this.state$.value.call;
    if (!call) return;
    const speaker = !call.speaker;
    this.patchCall({ speaker });
    this.applyAudioRoute(speaker);
  }

  private applyAudioRoute(speaker: boolean): void {
    if (!isAudioRouteAvailable()) return;
    AudioRoute.setSpeaker({ on: speaker })
      .then(info => console.log('[Softphone] audio route', JSON.stringify(info)))
      .catch(err => console.error('[Softphone] audio route', err))
      // перемикання аудіосесії iOS може заглушити мікрофон WebView — перевіряємо й за потреби беремо його заново
      .then(() => setTimeout(() => this.ensureMicrophone(), 400));
  }

  // Мікрофон у з'єднанні заглушений/зупинений (iOS перериває захоплення при зміні аудіосесії) —
  // беремо мікрофон заново і підміняємо доріжку, не перериваючи розмову. Стан «вимкнений мікрофон» зберігаємо.
  private async ensureMicrophone(): Promise<void> {
    const handler = this.session?.sessionDescriptionHandler as Web.SessionDescriptionHandler | undefined;
    const sender = handler?.peerConnection?.getSenders().find(item => item.track?.kind === 'audio' || item.track === null);
    const track = sender?.track;
    if (!sender || (track && !track.muted && track.readyState === 'live')) return;
    if (this.micRecovering || this.micRecoveries >= 3) return;
    this.micRecovering = true;
    this.micRecoveries++;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      const fresh = stream.getAudioTracks()[0];
      if (!fresh) return;
      fresh.enabled = !this.state$.value.call?.muted;
      this.watchMicrophone(fresh);
      await sender.replaceTrack(fresh);
      track?.stop();
    } catch (err) {
      console.error('[Softphone] microphone', err);
    } finally {
      this.micRecovering = false;
    }
  }

  private watchMicrophone(track: MediaStreamTrack): void {
    track.addEventListener('mute', () => setTimeout(() => this.ensureMicrophone(), 300));
    track.addEventListener('ended', () => this.ensureMicrophone());
  }

  // ── підключення ────────────────────────────────────────────────────────────
  private async connect(): Promise<void> {
    // Перепідключення зупиняє старе з'єднання разом з розмовою (у логах АТС: «Removed contact … due to request»,
    // і дзвінок обривався через кілька секунд). Поки йде розмова — не чіпаємо з'єднання, перепідключимось після неї
    if (this.session && this.session.state !== SessionState.Terminated) {
      console.warn('[Softphone] reconnect postponed — call in progress', new Error().stack);
      diagLog('reconnect postponed — call in progress');
      this.reconnectAfterCall = true;
      return;
    }
    this.clearReconnect();
    this.connectingSince = Date.now();
    diagLog('connect');
    this.patch({ status: 'connecting', message: undefined });

    let config: SoftphoneConfigModel;
    try {
      config = await firstValueFrom(this.asteriskService.getSoftphoneConfig());
    } catch {
      return this.fail('Не вдалося отримати налаштування телефону');
    }
    if (!config.enabled) {
      this.patch({ status: 'off', message: config.reason });
      return;
    }

    // старе з'єднання відв'язуємо ДО зупинки: інакше його подія «реєстрацію знято» показувала помилку
    // і запускала ще одне перепідключення — статус блимав «Підключено / АТС не прийняла» по колу
    const previous = this.userAgent;
    this.userAgent = null;
    this.registerer = null;
    await previous?.stop().catch(() => {});

    this.domain = config.domain || null;
    this.username = config.username || null;
    const uri = UserAgent.makeURI(`sip:${config.username}@${config.domain}`);
    if (!uri) return this.fail('Невірна адреса АТС');

    const userAgent = new UserAgent({
      uri,
      displayName: config.displayName,
      authorizationUsername: config.username,
      authorizationPassword: config.password,
      // keep-alive: проксі (Apache) і NAT не закривають тихе з'єднання між реєстраціями
      transportOptions: { server: config.wssUrl, keepAliveInterval: 25 },
      sessionDescriptionHandlerFactoryOptions: {
        peerConnectionConfiguration: { iceServers: [{ urls: ICE_SERVERS }] },
        // запасний тайм-аут: зазвичай збір завершується раніше — щойно відома зовнішня адреса (див. фабрику нижче).
        // Без неї АТС за NAT не надішле звук; після холодного запуску STUN буває повільним — тож не поспішаємо
        iceGatheringTimeout: 5000,
      },
      // iPhone: звук розмови веде нативний WebRTC (WKWebView грає лише на гучномовець);
      // Android: WebView, але SDP віддаємо, щойно відома зовнішня адреса, а не після повного збору маршрутів
      sessionDescriptionHandlerFactory: isNativeMediaAvailable()
        ? nativeSessionDescriptionHandlerFactory(ICE_SERVERS)
        : SoftphoneService.webSdhWithEarlyIce(),
      logLevel: 'warn',
      delegate: {
        onInvite: invitation => this.zone.run(() => this.onIncoming(invitation)),
        onDisconnect: error => this.zone.run(() => {
          if (userAgent !== this.userAgent) return; // подія від уже заміненого з'єднання
          if (error && !this.stopped) this.fail('Зв\'язок з АТС втрачено, підключаємось знову…');
        }),
      },
    });
    this.userAgent = userAgent;

    try {
      await userAgent.start();
      const registerer = new Registerer(userAgent, { expires: 300 });
      this.registerer = registerer;
      registerer.stateChange.addListener(state => this.zone.run(() => {
        if (registerer !== this.registerer) return; // подія від уже заміненої реєстрації
        diagLog(`registerer ${state}`);
        if (state === RegistererState.Registered) this.patch({ status: 'registered', message: undefined });
        if (state === RegistererState.Unregistered && !this.stopped) this.fail('АТС не прийняла реєстрацію');
      }));
      await registerer.register();
      this.wasSuspended = false;
    } catch (err) {
      console.error('[Softphone] connect', err);
      diagLog(`connect failed: ${err}`);
      this.fail('Немає з\'єднання з АТС');
    }
  }

  private ensureConnected(): void {
    // підключення вже йде (застосунок щойно відкрили, і він саме реєструється) — не перериваємо його
    if (this.state$.value.status === 'connecting' && Date.now() - this.connectingSince < 15000) return;
    const connected = this.userAgent?.isConnected() && this.registerer?.state === RegistererState.Registered;
    if (!connected) this.connect();
  }

  private fail(message: string): void {
    this.patch({ status: 'error', message });
    if (this.stopped) return;
    this.clearReconnect();
    this.reconnectTimer = setTimeout(() => this.connect(), RECONNECT_DELAY_MS);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  // ── вхідний дзвінок ────────────────────────────────────────────────────────
  private onIncoming(invitation: Invitation): void {
    // вже розмовляємо — друга лінія в етапі 1 не підтримується
    if (this.hasLiveSession) {
      invitation.reject({ statusCode: 486 }).catch(() => {});
      return;
    }
    this.session = invitation;
    this.minimized$.next(false);
    const identity = invitation.remoteIdentity;
    this.patch({
      call: {
        number: identity.uri.user || '',
        displayName: identity.displayName || '',
        status: 'ringing',
        muted: false,
        speaker: false,
      },
    });
    this.ringtone.currentTime = 0;
    this.ringtone.play().catch(() => {});
    this.track(invitation);
  }

  private track(session: Session, onEstablished?: () => void, onFailed?: () => void, awaitClientAnswer = false): void {
    let established = false;
    session.stateChange.addListener(state => this.zone.run(() => {
      diagLog(`session ${session instanceof Inviter ? 'out' : 'in'} ${state}`);
      if (state === SessionState.Established) {
        established = true;
        this.micRecoveries = 0;
        this.attachRemoteAudio(session);
        const handler = session.sessionDescriptionHandler as Web.SessionDescriptionHandler | undefined;
        handler?.peerConnection?.getSenders().forEach(sender => {
          if (sender.track?.kind === 'audio') this.watchMicrophone(sender.track);
        });
        // WebView вмикає гучномовець — повертаємо звук у розмовний динамік (або як обрав користувач)
        this.applyAudioRoute(this.state$.value.call?.speaker ?? false);
        // WebView може ввімкнути гучномовець ще раз, коли запускає відтворення, — повторюємо вибір
        setTimeout(() => {
          if (this.session === session && session.state === SessionState.Established) this.applyAudioRoute(this.state$.value.call?.speaker ?? false);
        }, 1500);
        this.patchCall(awaitClientAnswer ? { status: 'calling' } : { status: 'active', startedAt: Date.now() });
        this.startQualityMonitor(session);
        onEstablished?.();
      }
      if (state === SessionState.Terminated) {
        if (this.reconnectAfterCall) {
          this.reconnectAfterCall = false;
          setTimeout(() => this.ensureConnected(), 0);
        }
        this.qualityMonitor?.stop();
        this.qualityMonitor = null;
        if (!established) onFailed?.();
        if (established && isAudioRouteAvailable()) AudioRoute.reset().catch(() => {});
        if (established) this.finishHistory();
        this.stopRingtone();
        this.remoteAudio.srcObject = null;
        if (this.session === session) this.session = null;
        this.minimized$.next(false);
        this.patch({ call: null });
      }
    }));
  }

  // якість зв'язку: індикатор на екрані дзвінка й адаптивний бітрейт власного звуку (call-quality.ts)
  private startQualityMonitor(session: Session): void {
    this.qualityMonitor?.stop();
    const handler = session.sessionDescriptionHandler as any;
    const source: QualitySource | null = handler instanceof NativeSessionDescriptionHandler ? handler.qualitySource()
      : handler?.peerConnection ? webQualitySource(handler.peerConnection) : null;
    if (!source) return;
    this.qualityMonitor = new CallQualityMonitor(source, quality => this.zone.run(() => {
      diagLog(`quality ${quality}`);
      this.patchCall({ quality });
    }));
    this.qualityMonitor.start();
  }

  // співрозмовник підняв трубку (внутрішній дзвінок) — починаємо рахувати час розмови
  markAnswered(): void {
    if (this.state$.value.call?.status === 'calling') this.patchCall({ status: 'active', startedAt: Date.now(), note: undefined });
  }

  setCallNote(note: string | undefined): void {
    if (this.state$.value.call?.status === 'calling') this.patchCall({ note });
  }

  private checkClientAnswered(calls: GpCallModel[] | null | undefined): void {
    const call = this.state$.value.call;
    if (call?.status !== 'calling' || !calls?.length) return;
    const sip = this.store.selectSnapshot(ProfileState.currentUser)?.code;
    const digits = call.number.replace(/\D/g, '').slice(-9);
    const answered = calls.some(item => !item.isDial && item.SIP_number === sip
      && !!digits && (item.phone || '').replace(/\D/g, '').endsWith(digits));
    if (answered) this.patchCall({ status: 'active', startedAt: Date.now() });
  }

  private attachRemoteAudio(session: Session): void {
    const handler = session.sessionDescriptionHandler as Web.SessionDescriptionHandler | undefined;
    const peer = handler?.peerConnection;
    if (!peer) return;
    // звук беремо прямо з приймачів з'єднання — не залежимо від того, коли SIP.js наповнить свій remoteMediaStream
    const tracks = peer.getReceivers().map(receiver => receiver.track).filter(track => track?.kind === 'audio');
    this.remoteAudio.srcObject = tracks.length ? new MediaStream(tracks) : handler.remoteMediaStream;
    this.remoteAudio.play().catch(err => console.error('[Softphone] audio', err));
  }

  // Стандартний обробник SIP.js чекає повного збору ICE-кандидатів (з кількома мережами — секунди тиші після «Прийняти»).
  // Зовнішня адреса (srflx від STUN) з'являється за 100–300 мс — щойно вона в SDP, далі не чекаємо.
  private static webSdhWithEarlyIce(): SessionDescriptionHandlerFactory {
    const factory = Web.defaultSessionDescriptionHandlerFactory();
    return (session, options) => {
      const handler = factory(session, options) as Web.SessionDescriptionHandler;
      handler.peerConnectionDelegate = {
        onicecandidate: event => {
          if (event.candidate?.candidate?.includes(' typ srflx')) setTimeout(() => handler.iceGatheringComplete(), 50);
        }
      };
      return handler;
    };
  }

  private static createRemoteAudio(): HTMLAudioElement {
    const audio = document.createElement('audio');
    audio.autoplay = true;
    audio.setAttribute('playsinline', '');
    audio.style.display = 'none';
    document.body.appendChild(audio);
    return audio;
  }

  private stopRingtone(): void {
    this.ringtone.pause();
    this.ringtone.currentTime = 0;
  }

  private patch(partial: Partial<SoftphoneState>): void {
    this.state$.next({ ...this.state$.value, ...partial });
  }

  private patchCall(partial: Partial<SoftphoneCall>): void {
    const call = this.state$.value.call;
    if (!call) return;
    this.patch({ call: { ...call, ...partial } });
    if (partial.status === 'active' && this.history && !this.history.connectedAt) CallHistory.connected(this.history);
  }

  private finishHistory(): void {
    const entry = this.history;
    this.history = null;
    if (entry) CallHistory.end(entry);
  }
}
